// Libraries
import React, { useRef, useState, useEffect } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import * as Yup from "yup";
// Hooks
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
// Styles
import styles from "../login.module.scss";
// Icons
import { ReactComponent as EditIcon } from "../icons/edit.svg";
import { ReactComponent as Spinner } from "../icons/spinner.svg";
import { ReactComponent as LogoIcon } from "../icons/SEPARTA.svg";
import logourl from "../icons/SEPARTA.svg";

// Components
import { TextField } from "@pga/textfield";
import { OtpInput } from "@pga/otp-input";
import { Timer } from "./components/timer";
// Services & Types
import Authontication from "services/authontication/services";
import {
  ConfirmAuthCode_request,
  RequestAuthCode_request,
} from "services/authontication/type";
import { LoginType_Enum } from "services/authontication/enums";
import localStorageService from "services/localStorageService";
// Utils
import { AUTHORIZATION_INFO } from "@pga/utils/constance/constance";
// Types
import { Props } from "./types";
import { TerminalType_Enum } from "services/sale/common/enums";
import { Modal, ModalSizes } from "shared-components/modal";
import PassengerServices from "services/sale/passenger/service";
import { ApiErrorAlert } from "shared-components/apiErrorAlert";
import { HiOutlineExclamation } from "react-icons/hi";
import {
  GetApplicationRules_Response,
  GetApplicationSettings_Response,
} from "services/sale/passenger/types";
import { useErrorBoundary } from "react-error-boundary";
import skeleton from "./skeleton.module.scss";
import { Captcha, useCaptcha } from "@pga/captcha";
import { checkBaseUrlSlash } from "@pga/utils/checkUrlSlash/checkUrlSlash";

export const OtpLogin = ({
  paltformType,
  setLoginType,
  usernamePasswordLogin,
  returnUrl,
  isCaptcha,
}: Props) => {
  const navigate = useNavigate();
  const otpLength = 6;
  const sAlert = withReactContent(Swal);
  const [inputValue, setInputValue] = useState<string>("");
  const [otpValue, setOtpValue] = useState<string>("");
  const [otpValueLastIndex, setOtpValueLastIndex] = useState<string>("");
  const [stage, setStage] = useState(1);
  const [otpTime, setOtpTime] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timesUp, setTimesUp] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<HTMLInputElement>(null);
  const zeroLead = /^0[0-9].*$/;
  const { showBoundary } = useErrorBoundary();
  const kioskSerialNo = window.location.pathname.split("/")[2];

  const [Settings, setSettings] = useState<GetApplicationSettings_Response>(
    null!
  );
  const [rules, setRules] = useState<GetApplicationRules_Response>(null!);
  const [disabledLogin, setDisabledLogin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // تنظیمات و اطلاعات واحد
  const getPassengerSettings = async () => {
    try {
      setLoading(true);
      const settingsData = await PassengerServices.GetApplicationSettings();
      if (settingsData.success) {
        setDisabledLogin(false);
        setSettings({
          hasApplicationRules: settingsData?.data?.hasApplicationRules,
          logo: settingsData?.data?.logo,
          description: settingsData?.data?.description,
        });
      } else {
        setDisabledLogin(true);
        setErrorMessage(settingsData.messages.join("<p></p>"));
      }
    } catch (error) {
      console.log("🚀 ~ file: view.tsx ~ getPassengerSettings ~ error:", error);
      showBoundary(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stage === 1) {
      getPassengerSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  useEffect(() => {
    if (otpValueLastIndex !== "" && isSubmitting === false) {
      setIsSubmitting(true);
      onLogin(true, otpValueLastIndex);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValueLastIndex]);

  const modalShowRef = useRef<any>(null);
  // با کلیک روی "قوانین و مقررات" , ای پی آی کال شود , اگر موفقیت آمیز بود , مودال باز شده و قوانین دریفاتی از ای پی آی در آن نمایش داده شود.
  const showModal = async () => {
    const rulesData = await PassengerServices.GetApplicationRules();
    try {
      if (rulesData.success) {
        setRules(rulesData?.data);
        modalShowRef.current.open();
      } else {
        ApiErrorAlert(rulesData.messages);
      }
    } catch (error) {
      console.log("🚀 ~ file: view.tsx ~ showModal ~ error:", error);
      showBoundary(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      phoneNumber: "",
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string()
        .matches(/^0?9[0-9][0-9]{8}$/, "شماره تماس معتبر نیست")
        .required(""),
    }),
    onSubmit: async (values) => {
      onLogin(stage > 1);
    },
  });
  async function enterKeyDowFn() {
    onLogin();
  }
  const params = useParams();

  const getPhoneNumber = () =>
    zeroLead.test(inputValue) ? inputValue : "0" + inputValue;

  function goToLogin(needCompleteProfile: any) {
    if (needCompleteProfile === true)
      navigate(`/passenger/CompleteRegister`, { state: returnUrl });
    else if (returnUrl) {
      navigate(returnUrl);
    } else {
      switch (paltformType) {
        case TerminalType_Enum.PassengerApp:
          navigate("/passenger");
          break;
        case TerminalType_Enum.Driver:
          navigate("/driver");
          break;
        // case TerminalType_Enum.Management:
        //   navigate("/management");
        //   break;
        case TerminalType_Enum.Counter:
          navigate("/sale-counter/counters");
          break;
        case TerminalType_Enum.Kiosk:
          navigate(`/kiosk/${params.id}/request`);
          break;
      }
    }
  }

  const apiRequest = async (
    dataToSubmit: any,
    requestApi: any,
    nextpage?: boolean,
    resenCode?: boolean
  ) => {
    try {
      // Api request

      const resData = await requestApi(dataToSubmit);

      if (resData.success) {
        if (nextpage) {
          setStage((prev) => prev + 1);
          if (resData.data) {
            // otpTime
            setOtpTime(resData.data.otpTime);
          }
        }

        if (stage > 1 && resenCode !== true) {
          if (resData.data) {
            localStorageService.set(AUTHORIZATION_INFO, resData.data);
            goToLogin(resData.data.needCompleteProfile);
          } else {
            sAlert.fire({
              title: "اطلاعات شما یافت نشد ",
              confirmButtonText: "تایید",
            });
          }
        }
      } else {
        sAlert.fire({
          title: renderError(resData?.messages),
          confirmButtonText: "تایید",
        });
      }
    } catch (error) {
      console.log("🚀 ~ file: view.tsx ~ error:", error);
      showBoundary(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (messages: string[]) => {
    const element = document.createElement("div");
    messages.forEach((message) => {
      element.innerHTML += `
        <span>${message}</span>
      `;
    });

    return element;
  };

  // Api and validation login handler
  const onLogin = async (confirmation?: boolean, token?: string) => {
    // Confirm auth code
    if (confirmation === true) {
      if (otpValue.length >= otpLength) {
        const dataToSubmit: ConfirmAuthCode_request = {
          phoneNumber: getPhoneNumber(),
          loginType: LoginType_Enum.otp,
          password: "",
          userName: "",
          token: token ? token : otpValue,
          platformType: paltformType,
        };

        apiRequest(dataToSubmit, Authontication.ConfirmAuthCode);

        // If otp input is empty
      } else {
        sAlert.fire({
          title: "لطفا کد ارسال شده را وارد کنید",
          confirmButtonText: "تایید",
        });
      }

      return;
    }

    // Request Code
    const dataToSubmit: RequestAuthCode_request = {
      platformType: paltformType,
      phoneNumber: getPhoneNumber(),
      captchaValue: isCaptcha ? captchaData.value : undefined,
      captchaId: isCaptcha && captchaData.id ? captchaData.id : undefined,
    };
    apiRequest(dataToSubmit, Authontication.RequestAuthCode, true);
  };

  // Resend code
  const resendCode = (e: React.FormEvent) => {
    e.preventDefault();
    setOtpValue("");
    setTimesUp(false);

    const dataToSubmit: RequestAuthCode_request = {
      platformType: paltformType,
      phoneNumber: getPhoneNumber(),
    };

    apiRequest(dataToSubmit, Authontication.RequestAuthCode, undefined, true);
  };

  //useCaptcha
  const captchaData = useCaptcha(
    process.env.REACT_APP_GET_CAPTCHA!,
    `/kiosk/${kioskSerialNo}/login`
  );
  const isValidCaptcha = !isCaptcha || !!captchaData.value;
  // Disabeler
  const disableButton = () => {
    if (stage > 1) {
      return otpValue.trim().length !== otpLength || isSubmitting;
    } else {
      return (
        inputValue.length <= 0 ||
        !formik.isValid ||
        isSubmitting ||
        !isValidCaptcha
      );
    }
  };

  useEffect(() => {}, [otpValue.trim().length]);

  // Just for focus the input in init
  useEffect(() => {
    if (stage > 1) {
    } else {
      inputRef.current?.focus();
      setOtpValue("");
    }

    return () => {
      setIsSubmitting(false);
    };
  }, [stage]);

  // If already logged in redirect
  useEffect(() => {
    const token = localStorageService.get(AUTHORIZATION_INFO);
    if (token && token.accessToken) {
      goToLogin(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paltformType, navigate]);

  const [dimensions, setDimensions] = useState(window.innerHeight);
  const handleResize = () => {
    setDimensions(window.innerHeight);
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize, false);
  }, []);

  // نمایش تصویر دیفالت در صورتی که تصویر انتخاب شده توسط کاربر نمایش داده نشود
  const [isImageError, setIsImageError] = useState<boolean>(false);
  const onError = () => {
    setIsImageError(true);
  };

  return (
    <>
      {loading ? (
        <div className={skeleton["skeleton-container"]}>
          <div>
            <div
              className={`skeleton ${skeleton["_operate"]} ${skeleton["_common"]}`}
            ></div>
            <div
              className={`skeleton ${skeleton["_operate"]} ${skeleton["_common"]}`}
            ></div>
          </div>

          <div
            className={`skeleton ${skeleton["_content"]} ${skeleton["_common"]}`}
          ></div>
        </div>
      ) : (
        <>
          {disabledLogin ? (
            <div className={styles["error-message"]}>{errorMessage}</div>
          ) : (
            <>
              <form
                style={{ height: dimensions }}
                className={styles["_container"]}
                onSubmit={(e) => {
                  e.preventDefault();
                  setIsSubmitting(true);
                  stage > 1 ? onLogin(true) : formik.handleSubmit(e);
                }}
              >
                {/* Content */}
                <div className={styles["_content"]}>
                  {/* Title */}
                  {paltformType === TerminalType_Enum.PassengerApp ||
                  paltformType === TerminalType_Enum.Kiosk ? (
                    <div>
                      <div className={styles["app-information"]}>
                        {Settings?.logo ? (
                          <img
                            src={
                              isImageError
                                ? logourl
                                : checkBaseUrlSlash() + Settings.logo
                            }
                            alt={Settings.description || "سپرتا"}
                            className={styles["app-information-logo"]}
                            onError={onError}
                          />
                        ) : (
                          <LogoIcon className={styles["_title"]} />
                        )}
                        {paltformType === TerminalType_Enum.PassengerApp ? (
                          <p className={styles["app-information-text"]}>
                            {Settings?.description && Settings.description}
                          </p>
                        ) : (
                          ""
                        )}
                      </div>
                      {stage > 1 ? (
                        <p className={styles["_title"]}>
                          کد ارسال شده را وارد کنید
                        </p>
                      ) : (
                        <p className={styles["_title"]}>
                          شماره موبایل خود را وارد کنید
                        </p>
                      )}
                    </div>
                  ) : (
                    <LogoIcon className={styles["_title"]} />
                  )}

                  {/* Input and area code */}
                  <div
                    className={styles["_phonenumber"]}
                    onSubmit={() => onLogin}
                  >
                    <TextField
                      autoComplete={
                        paltformType === TerminalType_Enum.Kiosk ? "off" : "on"
                      }
                      inputRef={inputRef}
                      inputFontSize="1.1rem"
                      type={
                        paltformType === TerminalType_Enum.Kiosk
                          ? "text"
                          : "number"
                      }
                      name="phoneNumber"
                      className={
                        paltformType === TerminalType_Enum.Kiosk
                          ? styles["_kioskInput"]
                          : styles["_input"]
                      }
                      onChange={(e) => {
                        formik.handleChange(e);
                        setInputValue(e.target.value);
                      }}
                      value={
                        stage > 1 && paltformType === TerminalType_Enum.Kiosk
                          ? formik.values.phoneNumber
                              ?.toString()
                              ?.replace(/^(\d{4})\d+(\d{3})$/, "$1****$2")
                          : formik.values.phoneNumber
                      }
                      // onChange={(e) => setInputValue(e.target.value)}
                      disabled={stage > 1}
                    />
                    <button
                      type="button"
                      onClick={() => setStage(1)}
                      disabled={isSubmitting === true || stage === 1}
                      className={styles["_area-code"]}
                    >
                      {stage > 1 ? <EditIcon /> : "98+"}
                    </button>
                  </div>
                  {formik.touched.phoneNumber || formik.errors.phoneNumber ? (
                    <div className={styles["_formik-error"]}>
                      {formik.errors.phoneNumber}
                    </div>
                  ) : null}

                  {/* OTP */}
                  {stage > 1 && (
                    <>
                      <div className={styles["_otp-container"]}>
                        <OtpInput
                          inputRef={otpRef}
                          onChange={(value, index) => {
                            if (
                              index === otpLength - 1 &&
                              index === value.trim().length - 1
                            ) {
                              setOtpValueLastIndex(value);
                            }
                            setOtpValue(value);
                            // }
                          }}
                          valueLength={otpLength}
                          value={otpValue}
                        />

                        <div className={styles["_timer-container"]}>
                          {timesUp ? (
                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                            <a
                              className={styles["_resend-code"]}
                              onClick={resendCode}
                            >
                              ارسال مجدد
                            </a>
                          ) : (
                            <>
                              <span>کد دریافت نشد؟</span>
                              {otpTime > 0 && (
                                <Timer
                                  timeLimit={otpTime}
                                  onTimesUp={() => {
                                    setTimesUp(true);
                                  }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      {Settings &&
                        Settings.hasApplicationRules &&
                        (paltformType === TerminalType_Enum.PassengerApp ||
                          paltformType === TerminalType_Enum.Kiosk) && (
                          <span className={styles["_rules-note"]}>
                            با وارد کردن کد
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a
                              style={{
                                cursor:
                                  rules?.rules! === null ? "auto" : "pointer",
                                pointerEvents:
                                  rules?.rules! === null ? "none" : "auto",
                              }}
                              onClick={showModal}
                              className={styles["_rules-note-link"]}
                            >
                              قوانین و مقررات
                            </a>
                            <div>سپرتا را قبول کرده اید</div>
                          </span>
                        )}
                    </>
                  )}

                  {paltformType === TerminalType_Enum.Driver &&
                    usernamePasswordLogin && (
                      <span
                        className={`${styles["_change-login-type"]} ${styles["_change-login-type-usepass"]}`}
                        onClick={() =>
                          setLoginType &&
                          setLoginType(LoginType_Enum.usernamePassword)
                        }
                      >
                        ورود با نام کاربری و کلمه عبور
                      </span>
                    )}
                </div>

                {/* captcha */}
                {!(stage > 1) && isCaptcha ? (
                  <div>
                    <Captcha
                      data={{ ...captchaData, callBackFn: enterKeyDowFn }}
                    />
                  </div>
                ) : (
                  ""
                )}
                {/* Submit button */}
                <button
                  type="submit"
                  disabled={
                    isSubmitting || disableButton() || formik.isSubmitting
                  }
                  className={`btn btn-warning ${styles["_submit-btn"]}`}
                >
                  {stage > 1 ? "ورود" : "دریافت کد"}

                  {isSubmitting && <Spinner style={{ opacity: "0.5" }} />}
                </button>
              </form>
              <Modal
                componentId="ModalId"
                title="قوانین و مقررات"
                hasClose
                scrollable
                defaultSize={ModalSizes.md}
                icon={<HiOutlineExclamation color="rgb(236 119 0)" size={30} />}
                ref={modalShowRef}
              >
                <div className={styles["_rules"]}>
                  <pre className={styles["_rules--body"]}>{rules?.rules!}</pre>
                </div>
              </Modal>
            </>
          )}
        </>
      )}
    </>
  );
};
